var Emitter = require('../emitter')
var Timer = require('../timer')
var Constants = require('../constants')
var Util = require('../util')
var Log = require('../log')

var VideosRepository = require('../video/videos')
var NpawVideo = require('../video/video')
var Communication = require('../comm/communication')
var FlowTransform = require('../comm/transform/flow')
var FastDataTransform = require('../comm/transform/fastdata')
var ResourceTransform = require('../comm/transform/resource')
var OfflineTransform = require('../comm/transform/offline')

var Options = require('./options')
var NpawStorage = require('./storage')
var OfflineStorage = require('./offlineStorage')
var RequestBuilder = require('./requestbuilder')

var NpawAppAnalytics = require('../appanalytics/appanalytics')

var BrowserLoadTimes = require('../monitors/browserLoadTimes')
var BackgroundDetector = require('../detectors/backgroundDetector')
var DeviceDetector = require('../detectors/deviceDetector')
var UUIDGenerator = require('../deviceUUID/hashgenerator')

var YBRequest = require('../comm/request')

var Plugin = Emitter.extend(
  /** @lends npaw.Plugin.prototype */
  {
    /**
     * This is the main class of video analytics. You may want to have one instance for each video
     * you want to track. Will need {@link Adapter}s for both content and ads.
     *
     * @constructs Plugin
     * @extends npaw.Emitter
     * @memberof npaw
     *
     * @param {Options} [options] An object complying with {@link Options} constructor.
     * @param {Adapter} [adapter] If an adapter is provided, setAdapter will be immediately called.
     * @param {Object} [appAnalyticsDimensions] appAnalyticsDimensions
     */
    constructor: function (options, adapter, appAnalyticsDimensions) {
      /** Check if plugin was created previously */
      if (Plugin.instance) {
        if (options) {
          Log.notice('Set Options for already existing instance')
          Plugin.instance.setOptions(options)
          Plugin.instance.storage.updateStorageOptions(Plugin.instance.options.disableCookies, Plugin.instance.options.forceCookies, Plugin.instance.options.disableStorage)
        }
        if (adapter) {
          Log.notice('Set Adapter for already existing instance')
          Plugin.instance.setAdapter(adapter)
        }
        Log.warn('Only a single plugin instance can be created on npaw context')
        return Plugin.instance
      }

      /** Reference to {@link npaw.NpawStorage} */
      this.storage = new NpawStorage()

      /** Reference to Utils */
      this.utils = require('./../util')

      /** UUIDGenerator manager */
      this.uuidGenerator = new UUIDGenerator(this)

      /** Instance of npaw appanalytics. */
      this.appAnalytics = new NpawAppAnalytics(this)

      /** For legacy integration, support infinity naming for app analytics class */
      this.infinity = this.appAnalytics

      /** Adapter Templates */
      this.adapterTemplates = {}

      /** Create video structure */
      this.videos = new VideosRepository(this)

      /** Set Default Video object */
      this.videos.setVideo(new NpawVideo('default', this, options, adapter))

      /** Stored {@link Options} of the session. */
      this.options = new Options(options)

      /** Reference to {@link npaw.NpawStorage} */
      this.storage = new NpawStorage(null, this.options.disableCookies, this.options.forceCookies, this.options.disableStorage)

      /** Reference to {@link npaw.OfflineStorage} */
      if (!this.options.disableStorage) {
        this.offlineStorage = new OfflineStorage()
      }

      this._refreshData = new Timer(this._checkOldData.bind(this), 3600000) // 1h
      this._refreshData.start()

      this.requestBuilder = new RequestBuilder()

      this.resourceTransform = new ResourceTransform(this)

      this.lastEventTime = null

      this.restartFastDataTransform()

      this.appAnalytics._initAppAnalytics()

      this.browserLoadTimes = new BrowserLoadTimes(this)
      this.deviceDetector = new DeviceDetector()
      this.backgroundDetector = new BackgroundDetector(this)

      if (this.options['background.enabled']) {
        this.backgroundDetector.startDetection()
      }

      if (adapter) this.setAdapter(adapter)

      /** If AppAnalytics auto begin or AppAnalyticsDimensions are set, begin appAnalytics module */
      if (this.isAppAnalyticsAutoBegin() || appAnalyticsDimensions) {
        this.appAnalytics.begin(appAnalyticsDimensions)
      }

      this._logInitPluginEvent(options)

      /** Set instance on static variable */
      Plugin.instance = this
    },

    /**
     * Check Old Data
     * @private
     */
    _checkOldData: function () {
      if (this.videos.existsAdapter() && this.videos.getAdapter().flags.isStarted) return
      if (this.appAnalytics.appAnalyticsStarted) return
      this.restartFastDataTransform()
    },

    /**
     * Set Adapters Templates
     * @param adapters
     */
    _setAdapterTemplates: function (adapters) {
      this.adapterTemplates = adapters
    },

    /**
     *
     * @param response
     */
    restartFastDataTransform: function (response) {
      // FastData
      this.fastDataTransform = new FastDataTransform(this)
      this.fastDataTransform.on(FastDataTransform.Event.DONE, this._receiveData.bind(this))
      this.fastDataTransform.on(FastDataTransform.Event.CONFIGURATION_LOADED, this._receiveConfiguration.bind(this))

      // External response
      if (response) {
        this.fastDataTransform.setData(response)
        return
      }

      if (this.appAnalytics.getIsDataExpired() || !this.getStorageHost() ||
        ((this.storage.getLocal('accCode') !== this.options.accountCode) &&
          (this.storage.getSession('accCode') !== this.options.accountCode))) { // If expired or nonexistant
        this.storage.removeStorages('data')
        this.storage.removeStorages('session')
        this.storage.removeLocal('appAnalyticsStarted')
        this.fastDataTransform.init() // request a new data
      } else {
        this.fastDataTransform.loadConfiguration()
        this.fastDataTransform.setData(this.getStoredData()) // use stored data
      }
    },

    /**
     *
     * @param e
     * @private
     */
    _receiveConfiguration: function (e) {
      try {
        if (e.target.response.configurationOptions) {
          var configOptions = e.target.response.configurationOptions
          this.options.setOptions(configOptions, this.options)
          this.videos.updateAllOptions(configOptions)
        }
        if (this.isAppAnalyticsAutoBegin() && !this.appAnalytics._isModuleStarted()) {
          this.appAnalytics.begin()
        }
        if (this.isRefreshLMAConfigurationEnabled() && this.fastDataTransform) {
          this.fastDataTransform._createConfigurationInterval()
        }
      } catch (err) {}
    },

    /**
    * This callback is called when a correct data response is received.
    *
    * @param {any} e Response from fastdata
    */
    _receiveData: function (e) {
      this._logReceiveDataEvent(e)
      if (this.appAnalytics) {
        this.appAnalytics._beat.interval = e.target.response.beatTime * 1000
        this.appAnalytics.sessionExpire = e.target.response.sessionExpire * 1000
      }
      if (this.videos) {
        this.videos.updatePingInterval(e.target.response.pingTime * 1000)
        this.videos.updateAdapterTemplates(e.target.response.adapters)
      }
      this.storage.setStorages('data', e.target.response.msg)
      this.storage.setStorages('dataTime', new Date().getTime())
      this.storage.setStorages('accCode', this.options.accountCode)
      if (this.appAnalytics.getIsSessionExpired()) {
        this.fastDataTransform.setSession(this.fastDataTransform.response.code)
        this.storage.setStorages('session', this.fastDataTransform.response.code)
        this.storage.setStorages('host', this.fastDataTransform.response.host)
      } else {
        this.fastDataTransform.setSession(this.getSession())
        this.fastDataTransform.setHost(this.getStorageHost())
      }
    },

    /**
     * Process FastData response (Call directly from FastData class)
     * @param response
     */
    processFastDataResponse: function (response) {
      if (this.appAnalytics) {
        this.appAnalytics._beat.interval = response.beatTime * 1000
        this.appAnalytics.sessionExpire = response.sessionExpire * 1000
      }
      if (this.videos) {
        this.videos.updatePingInterval(response.pingTime * 1000)
        this.videos.updateAdapterTemplates(response.adapters)
      }
      this.storage.setStorages('data', response.msg)
      this.storage.setStorages('dataTime', new Date().getTime())
      this.storage.setStorages('accCode', this.options.accountCode)
      if (this.appAnalytics.getIsSessionExpired()) {
        this.fastDataTransform.setSession(this.fastDataTransform.response.code)
        this.storage.setStorages('session', this.fastDataTransform.response.code)
        this.storage.setStorages('host', this.fastDataTransform.response.host)
      } else {
        this.fastDataTransform.setSession(this.getSession())
        this.fastDataTransform.setHost(this.getStorageHost())
      }
    },

    /**
    * Reset all variables and stop all timers
     * @param {string} videoKey
    * @private
    */
    _reset: function (videoKey) {
      this.videos.resetVideo(videoKey)
    },

    /**
    * Initializes comm and its transforms.
    * @private
    */
    _initComm: function (videoKey) {
      videoKey = videoKey || 'default'
      var resource = ''
      if (this.videos.existsAdapter(videoKey)) {
        resource = this.videos.getAdapter(videoKey).getURLToParse()
        if (!resource) {
          resource = this.videos.getAdapter(videoKey).getResource()
        }
      }

      this.resourceTransform.init(resource)

      this._comm = new Communication()
      this._comm.addTransform(new FlowTransform())
      this._comm.addTransform(this.fastDataTransform)
      if (this.options && this.options.offline) {
        this._comm.addTransform(new OfflineTransform(this))
      } else {
        this._comm.addTransform(this.resourceTransform)
      }
    },

    /**
    * Returns the current {@link npaw.Communication} instance.
    *
    * @returns {npaw.Communication} communication instance
    */
    getComm: function () {
      return this._comm
    },

    /**
    * Modifies current options. See {@link Options.setOptions}.
    *
    * @param {any} options
    * @param {string} videoKey
    */
    setOptions: function (options, videoKey) {
      videoKey = videoKey || 'default'
      this._logSetOptionsEvent(options)
      if (options) {
        this.options.setOptions(options)
        if (typeof options['background.enabled'] === 'boolean') {
          if (options['background.enabled']) {
            this.backgroundDetector.startDetection()
          } else {
            this.backgroundDetector.stopDetection()
          }
        }
        if (videoKey && this.videos.existsVideo(videoKey)) {
          options = options || {}
          this.videos.getVideo(videoKey).mergeOptions(options)
        }
      }
    },

    /**
    * Disable request sending.
    */
    disable: function () {
      this.setOptions({ enabled: false })
    },

    /**
    * Re-enable request sending.
    */
    enable: function () {
      this.setOptions({ enabled: true })
    },

    // ----------------------------------------- LOGS -------------------------------------------

    _logInitPluginEvent: function (options) {
      var params = {
        logs: options,
        logAction: 'initPlugin',
        logType: 'pluginMethod'
      }
      this._sendPluginLogs(params)
    },

    _logSetAdapterEvent: function (adapterObject) {
      var params = {
        adapter: adapterObject,
        logAction: 'setAdapter',
        logType: 'pluginMethod'
      }
      this._sendPluginLogs(params, true)
    },

    _logSetOptionsEvent: function (options) {
      var params = {
        logs: options,
        logAction: 'setOptions',
        logType: 'pluginMethod'
      }
      this._sendPluginLogs(params, true)
    },

    _logReceiveDataEvent: function (fastDataObj) {
      var fdResponse = ''
      if (fastDataObj.data && fastDataObj.data.target && fastDataObj.data.target.response && fastDataObj.data.target.response.msg) {
        fdResponse = JSON.parse(fastDataObj.data.target.response.msg)
      }
      var params = {
        logs: fdResponse,
        logAction: 'receiveData',
        logType: 'internalMethod'
      }
      this._sendPluginLogs(params)
    },

    /**
     *
     * @param {*} params
     * @param postMethod
     */
    _sendPluginLogs: function (params, postMethod) {
      postMethod = postMethod || false
      if (this.isPluginLogsEnabled()) {
        if (params && params.logType && params.logAction) {
          Log.notice('PluginLog ' + params.logType + ': Action ' + params.logAction)
        }
        var request = null
        if (this._comm) {
          params = this.requestBuilder.buildParams(params, Constants.Service.VIDEO_PLUGIN_LOGS, this)
          if (params !== null) {
            request = new YBRequest(null, Constants.Service.VIDEO_PLUGIN_LOGS, params)
            if (postMethod) {
              request.setPostRequest(true)
            }
            this._comm.sendRequest(request)
          }
        } else if (this.appAnalytics._comm) {
          params = this.requestBuilder.buildParams(params, Constants.Service.APP_ANALYTICS_PLUGIN_LOGS, this)
          if (params !== null) {
            request = new YBRequest(null, Constants.Service.APP_ANALYTICS_PLUGIN_LOGS, params)
            if (postMethod) {
              request.setPostRequest(true)
            }
            this.appAnalytics._comm.sendRequest(request)
          }
        }
      }
    }
  },

  /** @lends npaw.Plugin */
  {
    // Static Memebers //
    /**
    * List of events that could be fired
    * @enum
    * @event
    */
    Event: Constants.WillSendEvent
  }
)

// Apply Mixins
// Plugin is actually a big class, I decided to separate the logic into
// different mixin files to ease the maintainability of each file.
// Filename convention will be plugin+xxxxx.js where xxxxx is the added functionality.
Util.assign(Plugin.prototype, require('./plugin_adapter'))
Util.assign(Plugin.prototype, require('./plugin_ads'))
Util.assign(Plugin.prototype, require('./plugin_common_getters'))
Util.assign(Plugin.prototype, require('./plugin_fire'))
Util.assign(Plugin.prototype, require('./plugin_storage'))

Plugin.instance = null

module.exports = Plugin
